<template>
	<div class="flex flex-col items-start gap-2">
		<SectionHeader :title="label ?? t('common.plantings')" />

		<!-- Main selector with info toggle -->
		<div class="flex w-full gap-2">
			<ion-button class="flex-1" color="semilight" @click="openModal">
				<div class="flex w-full items-center justify-between">
					<span>{{ buttonText }}</span>
					<ion-icon :icon="chevronForward" class="ml-2" />
				</div>
			</ion-button>

			<!-- Info toggle button -->
			<ion-button
				v-if="props.showPlantingInfo && selectedPlantingIds.length > 0"
				color="medium"
				fill="outline"
				size="default"
				class="flex-shrink-0"
				@click="toggleInfo"
			>
				<ion-icon
					:icon="showInfo ? chevronUp : informationCircleOutline"
				/>
			</ion-button>
		</div>

		<!-- Planting info display component -->
		<PlantingInfoDisplay
			v-if="
				props.showPlantingInfo &&
				showInfo &&
				selectedPlantingIds.length > 0
			"
			:planting-ids="selectedPlantingIds"
		/>

		<!-- Inactive Planting Warning -->
		<div
			v-if="inactiveSelectedPlantings.length > 0"
			class="rounded-md border border-amber-200 bg-amber-50 p-3"
		>
			<div class="flex items-start">
				<ion-icon
					:icon="warningOutline"
					class="mr-2 mt-0.5 flex-shrink-0 text-amber-500"
				/>
				<div class="text-sm">
					<p class="font-medium text-amber-800">
						{{ t("note.inactive-planting-warning") }}
					</p>
					<p class="text-amber-700">
						{{
							t("note.following-plantings-inactive", {
								count: inactiveSelectedPlantings.length,
							})
						}}
					</p>
					<div class="mt-2 flex flex-wrap gap-1">
						<span
							v-for="planting in inactiveSelectedPlantings"
							:key="planting.id"
							class="inline-flex items-center rounded-full bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800"
						>
							{{ planting.name }} ({{ planting.statusText }})
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>

	<PlantingSelectorModal
		ref="plantingModalRef"
		v-model="selectedPlantingIds"
	/>
</template>

<script setup lang="ts">
	import { ref, computed } from "vue";
	import { IonButton, IonIcon } from "@ionic/vue";
	import {
		chevronForward,
		informationCircleOutline,
		chevronUp,
		warningOutline,
	} from "ionicons/icons";
	import PlantingSelectorModal from "@/components/ui/modals/PlantingSelectorModal.vue";
	import PlantingInfoDisplay from "@/components/feature/notes/PlantingInfoDisplay.vue";
	import { useDefaultPlantingsQuery } from "@/composables/useDefaultPlantingsQuery";
	import SectionHeader from "@/components/feature/notes/SectionHeader.vue";
	import { PlantingDTO } from "seedgreen-shared/dtos/PlantingDTO";
	import { useI18n } from "vue-i18n";

	const { t } = useI18n();

	interface Props {
		placeholder?: string;
		label?: string;
		showPlantingInfo?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {
		placeholder: undefined,
		label: undefined,
		showPlantingInfo: true,
	});

	const defaultPlaceholder = computed(() => t("note.select-plantings"));

	const selectedPlantingIds = defineModel<number[]>({
		default: () => [],
	});

	const plantingModalRef = ref<InstanceType<typeof PlantingSelectorModal>>();
	const showInfo = ref(false);

	const { plantings } = useDefaultPlantingsQuery();

	const buttonText = computed(() => {
		if (selectedPlantingIds.value.length > 0) {
			return selectedPlantingIds.value
				.map((id) => {
					const planting = plantings.value?.find((p) => p.id === id);
					return planting ? planting.name : t("common.unknown");
				})
				.join(", ");
		}
		return props.placeholder ?? defaultPlaceholder.value;
	});

	const inactiveSelectedPlantings = computed(() => {
		if (!plantings.value) return [];

		return selectedPlantingIds.value
			.map((id) => plantings.value?.find((p) => p.id === id))
			.filter(
				(planting) => planting && !planting.isLive,
			) as PlantingDTO[];
	});

	function openModal() {
		plantingModalRef.value?.openModal();
	}

	function toggleInfo() {
		showInfo.value = !showInfo.value;
	}
</script>
